const ValidationUtilities = {

  commonVarChar() {
    return {
      regEx: /^[a-zA-Z0-9\s-+$*%./]{1,255}$/i,
      description: 'Valid input: ' + [
        'letters',
        'numbers',
        'spaces',
        'the dash (-)',
        'the plus (+)',
        'the dollar ($)',
        'the asterisk (*)',
        'the percent (%)',
        'the period (.)',
        'the slash (/)',
      ].join(', ') + '. Maximum length: 255 characters',
    }
  },
  email() {
    return {
      regEx: /^[\w!#$%&'*+\/=?`{|}~^\-]+(?:\.[\w!#$%&'*+\/=?`{|}~^\-]+)*@(?:[A-Z0-9-]+\.)+[A-Z]{2,6}$/i,
      description: 'Valid email format only',
    }
  },
  numbersOnly() {
    return {
      regEx: /^[0-9]+$/i,
      description: 'Valid input: ' + [
        'numbers',
      ].join(', '),
    }
  },
  lettersOnly() {
    return {
      regEx: /^[a-zA-Z]+$/i,
      description: 'Valid input: ' + [
        'letters',
      ].join(', '),
    }
  },
  lettersNumbersOnly() {
    return {
      regEx: /^[a-zA-Z0-9]+$/i,
      description: 'Valid input: ' + [
        'letters',
        'numbers',
      ].join(', '),
    }
  },
  sku() {
    return {
      regEx: /^[a-zA-Z0-9-. ]+$/i,
      description: 'Valid input: ' + [
        'letters',
        'numbers',
        'spaces',
        'the dash (-)',
        'the period (.)',
      ].join(', '),
    }
  },
  customerPONumber() {
    return {
      regEx: /^[a-zA-Z0-9\s-+$*%./]{1,30}$/i,
      description: 'Valid input: ' + [
        'letters',
        'numbers',
        'spaces',
        'the dash (-)',
        'the plus (+)',
        'the dollar ($)',
        'the asterisk (*)',
        'the percent (%)',
        'the period (.)',
        'the slash (/)',
      ].join(', ') + '. Maximum length: 30 characters',
    }
  },
  customerReferenceNumber() {
    return {
      regEx: /^[a-zA-Z0-9\s-+$*%./]{1,30}$/i,
      description: 'Valid input: ' + [
        'letters',
        'numbers',
        'spaces',
        'the dash (-)',
        'the plus (+)',
        'the dollar ($)',
        'the asterisk (*)',
        'the percent (%)',
        'the period (.)',
        'the slash (/)',
      ].join(', ') + '. Maximum length: 30 characters',
    }
  },
  streetAddressStrict() {
    return {
      regEx: /^[a-zA-Z0-9\s-./]+$/i,
      description: 'Valid input: ' + [
        'letters',
        'numbers',
        'spaces',
        'the hyphen (-)',
        'the period (.)',
        'the slash (/)',
      ].join(', '),
    }
  },
  /**
   * Preferentially we would use 'streetAddressStrict', but this will have to do until we can clean up the street name data
   * @return {{regEx: RegExp, description: string}}
   */
  streetAddressLenient() {
    return {
      regEx: /^[a-zA-Z0-9\s-.,'()#&/]+$/i,
      description: 'Valid input: ' + [
        'letters',
        'numbers',
        'spaces',
        'the hyphen (-)',
        'the period (.)',
        'the comma (,)',
        'the apostrophe (\')',
        'the left parenthetical (()',
        'right parenthetical ())',
        'the number (#)',
        'the ampersand (&)',
        'the slash (/)',
      ].join(', '),
    }
  },
  city() {
    return {
      regEx: /^[a-zA-Z\s-.()]+$/i,
      description: 'Valid input: ' + [
        'letters',
        'spaces',
        'the hyphen (-)',
        'the period (.)',
        'the left parenthetical (()',
        'right parenthetical ())',
      ].join(', '),
    }
  },
  postalCode() { // currently supports US, Mexico and Canadian postal code formats
    return {
      regEx: /^[ABCEGHJKLMNPRSTVWXYZ0-9\s-]+$/,
      description: 'Valid input: ' + [
        'specific capital letters (ABCEGHJKLMNPRSTVWXYZ)',
        'numbers',
        'spaces',
        'the hyphen (-)',
      ].join(', '),
    }
  },
  passwordReset() {
    const atLeastOneLowercaseLetter = '(?=.*[a-z])'
    const atLeastOneUppercaseLetter = '(?=.*[A-Z])'
    const atLeastOneNumeric = '(?=.*[0-9])'
    const withinLengthConstraintsWithoutWhitespaceChars = '(^[^\\s]{8,30}$)'
    return {
      regEx: new RegExp([
        atLeastOneLowercaseLetter,
        atLeastOneUppercaseLetter,
        atLeastOneNumeric,
        withinLengthConstraintsWithoutWhitespaceChars,
      ].join(''), 'g'),
      description: 'Minimum length: 8 characters. Maximum length: 30 characters. ' + capitalizeFirstLetter([
        'whitespace characters',
      ].join(', ')) + ' are not permitted.',
    }
  },
  /**
   * @param {string} password
   * @return {{regEx: RegExp, description: string}}
   */
  passwordResetConfirmation(password) {
    return {
      regEx: new RegExp('^' + escapeCommonRegExpTokens(password) + '$'),
      description: 'Password does not match',
    }
  },
}

/**
 * Capitalize the first letter of the string
 *
 * @param {string} str
 * @return {string}
 */
const capitalizeFirstLetter = (str) => {
  return str.split('').map((char, index) => index === 0 ? char.toUpperCase() : char).join('')
}

/**
 * Escapes common regular expression special characters in a given text string.
 *
 * This function takes a string and escapes characters that have special meaning in regular expressions,
 * such as brackets, parentheses, asterisks, and other symbols. The escaped string can then be safely
 * used in the construction of a regular expression without unintentional pattern interpretation or errors.
 *
 * @param {string} text - The input string containing characters to be escaped for safe use in a regular expression.
 * @returns {string} - A new string with special regular expression characters escaped.
 */
const escapeCommonRegExpTokens = (text) => {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&")
}

export default ValidationUtilities
